import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteSubjectPublicationClaims,
  deleteSubjectStoryClaims,
  getCurrentUserGroupsIds,
  saveSubjectPublicationClaims,
  saveSubjectStoryClaimWithSync
} from '../../api/subjectClaimsApi';
import { subjectClaimsQueryKeys } from './subjectClaimsQueryKeys';
import { useToast } from '../../components/Toast/context/useToast.context';
import { subjectClaims as subjectClaimsMsgs } from '../../../locale/stories.en.locale.json';
import {
  ISubjectPublicationClaimsList,
  ISubjectStoryClaimsList
} from '../../../types';

function getSubjectClaimsMsgs() {
  return subjectClaimsMsgs;
}

export function useCurrentUserGroupsIds(params: { subjectId: string; entityUuid: string } | null) {
  const { toggleToast } = useToast();
  const msgs = getSubjectClaimsMsgs();

  return useQuery({
    queryKey: params
      ? subjectClaimsQueryKeys.userGroups(params)
      : ([...subjectClaimsQueryKeys.all, 'userGroups', 'disabled'] as const),
    queryFn: () => getCurrentUserGroupsIds(params!.subjectId, params!.entityUuid),
    enabled: params !== null && !!params.subjectId && !!params.entityUuid,
    onError: () => {
      toggleToast({
        title: msgs?.somethingWentWrongError ?? 'Something went wrong',
        body: msgs?.failedToFetchSubjectGroupIds ?? 'Failed to fetch subject group ids',
        severity: 'error'
      });
    }
  });
}

export function useSaveSubjectStoryClaim() {
  const { toggleToast } = useToast();
  const queryClient = useQueryClient();
  const msgs = getSubjectClaimsMsgs();

  return useMutation({
    mutationFn: (params: {
      subjectStoryClaimsList: ISubjectStoryClaimsList;
      publicationId: string;
      sectionIds: string[];
      notifyNewCollaborators?: boolean;
      notifyBulkCollaborators?: boolean;
    }) =>
      saveSubjectStoryClaimWithSync({
        ...params,
        manageShared: msgs?.manageShared ?? 'manage_shared'
      }),
    onSuccess: () => {
      // Refresh story/publication claims everywhere (row locks, story-view access,
      // and the access dialogs' preloaded lists) so saved access reflects without refresh.
      void queryClient.invalidateQueries({ queryKey: subjectClaimsQueryKeys.all });
      toggleToast({
        title: msgs?.accessUpdated ?? 'Access updated',
        body: msgs?.successfullyUpdateSubjectClaim ?? 'Changes made have been updated.',
        severity: 'success'
      });
    },
    onError: () => {
      toggleToast({
        title: msgs?.somethingWentWrongError ?? 'Something went wrong',
        body: msgs?.failedToSaveSubjectClaims ?? 'Failed to save subject claims',
        severity: 'error'
      });
    }
  });
}

export function useDeleteSubjectStoryClaim() {
  const { toggleToast } = useToast();
  const queryClient = useQueryClient();
  const msgs = getSubjectClaimsMsgs();

  return useMutation({
    mutationFn: (params: { subjectIds: string[]; storyId: string }) => deleteSubjectStoryClaims(params.subjectIds, params.storyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: subjectClaimsQueryKeys.all });
    },
    onError: () => {
      toggleToast({
        title: msgs?.somethingWentWrongError ?? 'Something went wrong',
        body: msgs?.failedToDeleteSubjectClaim ?? 'Failed to delete subject claim',
        severity: 'error'
      });
    }
  });
}

export function useSaveSubjectPublicationClaim() {
  const { toggleToast } = useToast();
  const msgs = getSubjectClaimsMsgs();

  return useMutation({
    mutationFn: (params: {
      subjectPublicationClaimsList: ISubjectPublicationClaimsList;
      publicationId: string;
    }) => saveSubjectPublicationClaims(params.subjectPublicationClaimsList, params.publicationId),
    onSuccess: () => {
      toggleToast({
        title: msgs?.accessUpdated ?? 'Access updated',
        body: msgs?.successfullyUpdateSubjectClaim ?? 'Changes made have been updated.',
        severity: 'success'
      });
    },
    onError: () => {
      toggleToast({
        title: msgs?.somethingWentWrongError ?? 'Something went wrong',
        body: msgs?.failedToSaveSubjectClaims ?? 'Failed to save subject claims',
        severity: 'error'
      });
    }
  });
}

export function useDeleteSubjectPublicationClaim() {
  const { toggleToast } = useToast();
  const msgs = getSubjectClaimsMsgs();

  return useMutation({
    mutationFn: (params: {
      subjectIds: string[];
      publicationId: string;
      entityId: number;
      entityUuid: string;
    }) => deleteSubjectPublicationClaims(params),
    onError: () => {
      toggleToast({
        title: msgs?.somethingWentWrongError ?? 'Something went wrong',
        body: msgs?.failedToDeleteSubjectClaim ?? 'Failed to delete subject claim',
        severity: 'error'
      });
    }
  });
}
